import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, XCircle, Crown } from "lucide-react";
import MobileShell from "@/components/MobileShell";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshSubscription, refresh, refreshCredits } = useAuth();
  const { t } = useLang();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    let active = true;
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await client.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await Promise.all([refreshSubscription(), refresh(), refreshCredits()]);
          if (active) setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { if (active) setStatus("error"); return; }
      } catch { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { if (active) setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, refreshSubscription, refresh, refreshCredits]);

  return (
    <MobileShell>
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-8">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="animate-spin text-violet-400 mx-auto" size={48} />
            <h1 className="font-heading text-xl font-extrabold text-white mt-5">{t("pay_checking")}</h1>
            <p className="text-slate-400 text-sm mt-1">{t("pay_checking_sub")}</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-success" className="animate-float-up">
            <span className="w-20 h-20 rounded-full mel-gradient flex items-center justify-center text-white mx-auto shadow-[0_16px_40px_-10px_rgba(37,99,235,0.9)]"><Crown size={38} /></span>
            <h1 className="font-heading text-2xl font-extrabold text-white mt-5">{t("pay_ok_title")}</h1>
            <p className="text-slate-300 mt-2">You're now MELLETIC PRO — your full A1–C2 journey with AI practice included.</p>
            <button onClick={() => nav("/home")} data-testid="go-home-btn" className="mt-6 w-full mel-gradient text-white font-bold py-3.5 rounded-2xl press">{t("start_speaking")}</button>
          </div>
        )}
        {(status === "error" || status === "timeout") && (
          <div data-testid="payment-error">
            <XCircle className="text-red-400 mx-auto" size={48} />
            <h1 className="font-heading text-xl font-extrabold text-white mt-5">{t("pay_err_title")}</h1>
            <p className="text-slate-400 text-sm mt-1">{t("pay_err_sub")}</p>
            <button onClick={() => nav("/upgrade")} className="mt-6 w-full glass text-white font-semibold py-3.5 rounded-2xl press" data-testid="back-upgrade-btn">{t("back_to_sub")}</button>
            <button onClick={() => nav("/home")} className="mt-2 w-full text-slate-400 font-medium py-2 press">{t("back_home")}</button>
          </div>
        )}
      </div>
    </MobileShell>
  );
}
